import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiTranscription = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-transcription',
			name: 'AI Transcription: Turn Interviews, Meetings, and Lectures into Text',
			description:
				"Transcribe any audio or video recording with Lift's transcript generator, then clean up filler words or add a short summary. Try Lift today!",
			url: 'https://liftapp.ai/ai-transcription',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-transcription#product' },
				{ '@id': 'https://liftapp.ai/ai-transcription#howto' },
				{ '@id': 'https://liftapp.ai/ai-transcription#benefits' },
				{ '@id': 'https://liftapp.ai/ai-transcription#faq' },
				{ '@id': 'https://liftapp.ai/ai-transcription#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-transcription#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-transcription#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-transcription#product',
			name: 'Online AI Transcription Tool',
			description:
				"Upload a file, and Lift's AI transcriber turns what was said into text you can copy or download. Try Lift today!",
			url: 'https://liftapp.ai/ai-transcription',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-transcription#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload an audio or video file',
					text: 'Add a recording from your device, from a quick voice note to a full-length meeting.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Pick the language and audio cues',
					text: "Keep Auto to detect what's spoken, or set it yourself and choose whether to mark background sounds.",
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Start the AI transcription',
					text: 'Tap Generate, and Lift converts the speech into text, saving every result in My Transcriptions.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Polish and download the result',
					text: 'Clean up filler words, add a summary, then copy the text or save a TXT or PDF.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-transcription#benefits',
			name: 'Who can benefit from AI Transcription?',
			description:
				"Lift's AI Transcription helps journalists, students, researchers, teams, and support leads turn recorded speech into clean, searchable text.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Journalists — Transcribe recorded interviews and pull exact quotes without replaying the audio dozens of times.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'University students — Turn lecture recordings into text and generate a summary to review before exams.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Market researchers — Convert focus group sessions into searchable text and spot recurring themes across participants.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Remote teams — Get text from meeting recordings, then share a short recap with colleagues who missed the call.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Customer support leads — Review call recordings as text and remove filler words before sharing examples with agents.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl: 'https://liftapp.ai/images/ai-transcription/benefits.webp',
				name: 'AI Transcription by Lift',
				description:
					'Tablet on a conference table showing a meeting transcript generated by Lift, with colleagues talking behind',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-transcription#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Transcription?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Transcription turns speech from audio and video files into written text. You can then clean up the transcript, summarize it, and download the result.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Transcription work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload a file, keep the language on Auto or choose it, and tap Generate. Open the finished transcript to copy it, clean it up, summarize it, or download it.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can the transcript include background sounds?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Audio Cues is on by default and marks non-verbal noises like laughter or wind in the text. Turn it off if you only need the spoken words.',
					},
				},
				{
					'@type': 'Question',
					name: 'How can I save my transcript?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Use Copy to send everything to your clipboard, or Download to get a TXT or PDF file. If you also created a summary, export it on its own or together with the transcription.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my audio and video files to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your files securely and uses them only to generate your transcript.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-transcription#why-lift',
			name: 'Why choose Lift for AI transcription',
			description:
				"Lift's AI Transcription keeps every recording, transcript, and summary in one affordable tool available on iOS, Android, and Web.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl: 'https://liftapp.ai/images/ai-transcription/whyLift.webp',
				name: 'Why use Lift AI Transcription',
				description:
					"Laptop on a wooden desk showing Lift's transcript generator with a language picker and saved files",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-transcription#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-transcription#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Transcription',
					item: 'https://liftapp.ai/ai-transcription',
				},
			],
		},
	],
};
